import type { ChapterRegisterMember } from "../api/chapterApi";

/**
 * Downloads of a chapter's membership register, as its chairman sees it.
 *
 * The register exists to be worked from off the screen — a meeting notice, a
 * quorum count, a list to read names off at an AGM — so it has to leave the
 * browser in the two forms those jobs take: a spreadsheet to sort and filter,
 * and a sheet to print and carry.
 *
 * Both are built from the SAME helpers the table renders with (fullName,
 * genderLabel, standing below), so a download cannot disagree with the screen
 * it was taken from. Both carry a header naming the chapter, the filters that
 * were on and the day it was taken — a list of members with no statement of
 * which members it is a list of is worthless a week later.
 *
 * Dependency-free, like the upgrade exports: CSV with a UTF-8 BOM, which Excel
 * opens directly, and a styled print window the browser's own "Save as PDF"
 * writes. The portal ships no spreadsheet or PDF library and printing a table
 * is not the reason to add one.
 *
 * There is no money in here and no document, because there is none in the
 * register — dues read as up to date or not, exactly as on screen.
 */

/* ── The row, as the table renders it ────────────────────────────────────── */

/** Title, surname, first and other names, as one name. */
export const fullName = (r: ChapterRegisterMember) =>
  [r.title, r.surname, r.firstname, r.othernames]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * The gender column was typed in by hand over many years — "M", "m", "Male",
 * "F", "Female" all sit in it. The register reads as one list, so the first
 * letter decides what is printed; the stored value is left alone.
 */
export const genderLabel = (raw?: string | null) => {
  const initial = String(raw ?? "").trim().charAt(0).toLowerCase();
  if (initial === "m") return "Male";
  if (initial === "f") return "Female";
  return "—";
};

/**
 * Standing, in the order it has to be read: deceased first — the register's own
 * flag outranks whatever the account says — then the account state, and
 * "No account" for a member who has never had one.
 */
export const standing = (r: ChapterRegisterMember) => {
  if (Number(r.deceased))
    return { label: "Deceased", tone: "bg-gray-100 text-gray-600 border-gray-200" };
  if (r.account_status === "active")
    return { label: "Active", tone: "bg-green-50 text-green-700 border-green-200" };
  if (r.account_status === "inactive")
    return { label: "Inactive", tone: "bg-red-50 text-red-600 border-red-200" };
  return { label: "No account", tone: "bg-gray-50 text-gray-500 border-gray-200" };
};

/**
 * The number a member is called by: the one issued on upgrade where they hold
 * one, their membership number otherwise. The same precedence the table uses.
 */
export const memberNumber = (r: ChapterRegisterMember) =>
  r.member_member_no || r.membership_no || "";

/* ── Meta ────────────────────────────────────────────────────────────────── */

export type ChapterRegisterExportMeta = {
  /** The chapter the register belongs to, for the heading and the filename. */
  chapter: string;
  /**
   * True only where the chairman covers a second chapter — then the rows come
   * from more than one register and each has to say which, the same way the
   * table grows the column.
   */
  withChapterColumn: boolean;
  /** The filters that were on when the download was taken, already worded. */
  filters: string[];
  /** How the screen was sorted, in words, so the order on paper is explained. */
  order?: string;
};

/* ── Columns ─────────────────────────────────────────────────────────────── */

const BASE_COLUMNS = [
  "Full Name",
  "Membership No",
  "Grade",
  "Email",
  "Gender",
  "Status",
  "Verification",
  "Dues",
  "Registered",
  "State",
  "LGA",
] as const;

/**
 * The columns of the download, which are the columns of the screen: the chapter
 * is inserted after the name exactly where the table inserts it, and only when
 * the table has it.
 */
export function columnsFor(meta: ChapterRegisterExportMeta): string[] {
  const columns: string[] = [...BASE_COLUMNS];
  if (meta.withChapterColumn) columns.splice(1, 0, "Chapter");
  return columns;
}

function toRow(r: ChapterRegisterMember, meta: ChapterRegisterExportMeta): string[] {
  const cells = [
    fullName(r) || "—",
    memberNumber(r) || "—",
    r.grade?.name ?? "—",
    r.email || "—",
    genderLabel(r.gender),
    standing(r).label,
    r.account_verified_at ? "Verified" : "Unverified",
    r.owes_dues ? "Not up to date" : "Up to date",
    r.reg_date ? String(r.reg_date).slice(0, 10) : "—",
    r.state?.name ?? "—",
    r.lga?.name ?? "—",
  ];

  if (meta.withChapterColumn) cells.splice(1, 0, r.chapter?.name ?? "—");
  return cells;
}

/* ── Filename and stamp ─────────────────────────────────────────────────── */

/** Today, for the "taken on" line and the filename. */
function stamp() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    printed: now.toLocaleString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    file: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
  };
}

const slug = (text: string) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "chapter";

const filename = (meta: ChapterRegisterExportMeta, extension: string) =>
  `${slug(meta.chapter)}-chapter-register-${stamp().file}.${extension}`;

const heading = (meta: ChapterRegisterExportMeta) =>
  `${meta.chapter} Chapter Register`;

/**
 * The standing of the members listed, counted off the rows being downloaded
 * rather than off the whole chapter — so the figures describe THIS list and not
 * one the reader cannot see.
 */
function summary(rows: ChapterRegisterMember[]) {
  const count = (test: (r: ChapterRegisterMember) => boolean) => rows.filter(test).length;

  return [
    { label: "Members listed", count: rows.length },
    { label: "Active", count: count((r) => standing(r).label === "Active") },
    { label: "Inactive", count: count((r) => standing(r).label === "Inactive") },
    { label: "No account", count: count((r) => standing(r).label === "No account") },
    { label: "Deceased", count: count((r) => standing(r).label === "Deceased") },
    { label: "Verified", count: count((r) => !!r.account_verified_at) },
    { label: "Dues up to date", count: count((r) => !r.owes_dues) },
    { label: "Dues not up to date", count: count((r) => !!r.owes_dues) },
  ];
}

/* ── Spreadsheet ─────────────────────────────────────────────────────────── */

/** RFC 4180: quote everything, and double any quote inside a field. */
const csvCell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const csvLine = (cells: unknown[]) => cells.map(csvCell).join(",");

/**
 * A membership number that survives being opened in Excel.
 *
 * Quoting is not enough: the member series starts at 06970, and Excel reads
 * "06970" as the NUMBER 6970 and drops the leading zero — leaving a column of
 * numbers that match nobody in the register. The ="…" form is the one escape
 * both Excel and Google Sheets honour as text. Applied by COLUMN rather than
 * inside toRow(), which is shared with the printed sheet — that would print the
 * escape instead of the number.
 */
const csvNumberCell = (value: unknown) => {
  const number = String(value ?? "").trim();
  return number && number !== "—" ? `"=""${number.replace(/"/g, '""')}"""` : csvCell(value);
};

export function exportChapterRegisterSpreadsheet(
  rows: ChapterRegisterMember[],
  meta: ChapterRegisterExportMeta,
) {
  const { printed } = stamp();
  const columns = columnsFor(meta);
  // Looked up, not hard-coded: the chapter column shifts it by one.
  const numberCol = columns.indexOf("Membership No");

  const dataLine = (cells: unknown[]) =>
    cells.map((cell, i) => (i === numberCol ? csvNumberCell(cell) : csvCell(cell))).join(",");

  const lines: string[] = [
    csvLine([`NIQS — ${heading(meta)}`]),
    ...meta.filters.map((f) => csvLine([f])),
    ...(meta.order ? [csvLine([`Order: ${meta.order}`])] : []),
    csvLine([`Taken on: ${printed}`]),
    "",
    csvLine(["Summary of the members listed"]),
    ...summary(rows).map((s) => csvLine([s.label, s.count])),
    "",
    csvLine(columns),
    ...rows.map((r) => dataLine(toRow(r, meta))),
  ];

  // The BOM is what makes Excel read this as UTF-8 rather than the local
  // codepage — without it an accented name arrives as mojibake.
  const blob = new Blob(["﻿" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename(meta, "csv");
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick — Safari cancels the download if the URL dies
  // before it has read it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── PDF (via the browser's print dialog) ────────────────────────────────── */

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * @param target A window the CALLER already opened, inside the click that asked
 *   for the download. The register has to be collected page by page first, and
 *   a window opened after that await has no user gesture behind it — every
 *   browser blocks it as a pop-up. Omitted, this opens its own, which is only
 *   safe when nothing was awaited first.
 */
export function exportChapterRegisterPdf(
  rows: ChapterRegisterMember[],
  meta: ChapterRegisterExportMeta,
  target?: Window | null,
) {
  const { printed } = stamp();
  const w = target ?? window.open("", "_blank", "width=1100,height=800");
  if (!w) {
    alert("Allow pop-ups for this site to print the register.");
    return;
  }

  // Clears whatever the caller put there while the rows were being collected;
  // a second write into an open document would append to it instead.
  w.document.open();

  const columns = columnsFor(meta);
  // Printed on paper, these must not break across two lines: a date that wraps
  // gets misread, and a membership number that wraps gets mistyped.
  const nowrapCols = [columns.indexOf("Membership No"), columns.indexOf("Registered")];

  const stats = summary(rows)
    .map(
      (s) =>
        `<div class="stat"><span class="n">${s.count}</span><span class="l">${esc(s.label)}</span></div>`,
    )
    .join("");

  const body = rows.length
    ? rows
        .map(
          (r) =>
            `<tr>${toRow(r, meta)
              .map((cell, i) =>
                nowrapCols.includes(i)
                  ? `<td class="nb">${esc(cell)}</td>`
                  : `<td>${esc(cell)}</td>`,
              )
              .join("")}</tr>`,
        )
        .join("")
    : `<tr><td class="empty" colspan="${columns.length}">No members match these filters.</td></tr>`;

  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(filename(meta, "pdf").replace(/\.pdf$/, ""))}</title>
  <style>
    @page{size:A4 landscape;margin:12mm}
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{color:#111;font-size:10px}
    .head{border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:14px}
    .org{font-size:9px;font-weight:900;letter-spacing:.22em;text-transform:uppercase;color:#888}
    h1{font-size:17px;font-weight:900;margin-top:4px}
    .sub{font-size:10px;color:#666;margin-top:5px;line-height:1.6}
    .sub b{color:#111}
    .stats{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:14px}
    .stat{border:1px solid #e6e6e6;border-radius:7px;padding:6px 10px;min-width:78px}
    .stat .n{display:block;font-size:15px;font-weight:900}
    .stat .l{display:block;font-size:8px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#999;margin-top:1px}
    table{width:100%;border-collapse:collapse}
    th{background:#111;color:#fff;font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase;text-align:left;padding:7px 6px}
    td{padding:6px;border-bottom:1px solid #eee;vertical-align:top}
    td.nb{white-space:nowrap}
    td.empty{text-align:center;color:#999;padding:24px}
    tr{page-break-inside:avoid}
    thead{display:table-header-group}
    .foot{margin-top:14px;padding-top:8px;border-top:1px solid #eee;font-size:8px;color:#aaa;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
  </style>
</head>
<body>
  <div class="head">
    <div class="org">Nigerian Institute of Quantity Surveyors</div>
    <h1>${esc(heading(meta))}</h1>
    <div class="sub">
      ${rows.length} member(s) listed${meta.filters.length ? ` &middot; ${meta.filters.map(esc).join(" &middot; ")}` : ""}${
        meta.order ? ` &middot; ${esc(meta.order)}` : ""
      }<br>
      Taken on ${esc(printed)}
    </div>
  </div>

  <div class="stats">${stats}</div>

  <table>
    <thead><tr>${columns.map((c) => `<th>${esc(c)}</th>`).join("")}</tr></thead>
    <tbody>${body}</tbody>
  </table>

  <div class="foot">NIQS Portal &middot; Chapter Membership Register</div>
  <!-- onload rather than an immediate call, so the sheet is laid out before the
       print dialog snapshots it; the timeout is the fallback, and the guard
       stops the two paths both printing. -->
  <script>
    (function () {
      var printed = false;
      function go() { if (printed) return; printed = true; window.print(); }
      window.onload = go;
      setTimeout(go, 4000);
    })();
  </script>
</body>
</html>`);
  w.document.close();
}
